/**
 * dashboardDrilldownRegistry.ts
 *
 * Central registry mapping every metric_key to:
 *   - source query / loader function
 *   - display metadata
 *   - allowed roles (RBAC)
 *   - sensitive data flag (triggers audit log + field masking)
 *   - default filters
 *   - column definitions for the drill-down table
 *   - route to open source record in context
 *
 * Pattern: add a new entry here + a drill-down loader in drilldowns-extended.ts,
 * then call open(metric_key) from any card — the drawer picks up the rest.
 */

import type { DrilldownResult } from './queries/drilldowns';
import {
  drillSalesPipeline, drillWonDealsMtd, drillOutstandingAR,
  drillOutstandingAP, drillCashPosition, drillActiveProjects,
  drillHeadcount, drillOpenNcrs,
} from './queries/drilldowns';
import {
  drillRevenueMtd, drillOverdueReceivables, drillPayrollCost,
  drillInventoryValue, drillProductionOrders, drillProjectBudget,
  drillZatcaRejected, drillApprovalAging, drillOpenRisks,
  drillGosiContributions, drillWpsSubmissions, drillLeaveBalance,
  drillIqamaExpiry, drillNitaqatByDept, drillTrainingCompletion,
  drillDisciplinaryCases, drillOpenPurchaseOrders,
  drillPendingSupplierInvoices, drillThreeWayMatchExceptions,
  drillStockouts, drillReorderAlerts, drillSlowMovingStock,
  drillCycleCountVariances, drillMrpExceptions, drillWorkOrders,
  drillMilestoneDelays, drillProjectRisks,
  drillAuditFindings, drillPdplRequests, drillZatcaGaps,
  drillPendingApprovals, drillRejectedApprovals, drillEscalatedApprovals,
  drillEbitda, drillVatLiability, drillBankReconciliationExceptions,
  drillDepreciationRuns, drillLeadsByStage, drillQuotationsByStatus,
  drillSalesRepPerformance, drillCommissionPayable, drillMfaViolations,
  drillDocumentExpiry, drillPayrollBatches,
  drillCrmNewLeads, drillCrmQualifiedLeads, drillCrmConvertedLeads,
  drillCrmOpenOpportunities, drillCrmPipelineValue, drillCrmWeightedPipeline,
  drillCrmQuotationSent, drillCrmWonValue, drillCrmLostValue,
  drillCrmLostReasons, drillCrmOverdueActivities, drillCrmStaleOpportunities,
  drillCrmExpiringQuotations, drillCrmTargetAchievement,
  drillCrmRevenueByChannel, drillCrmRevenueBySector, drillCrmMarginExceptions,
  drillCrmPendingApprovals, drillCrmCreditHoldCustomers, drillCrmCollectionRisk,
  drillCrmRevenueByProductCategory, drillCrmDormantCustomers,
} from './queries/drilldownsExtended';

// ── Types ────────────────────────────────────────────────────

export type MetricModule =
  | 'finance' | 'sales' | 'hr' | 'payroll' | 'procurement'
  | 'warehouse' | 'manufacturing' | 'projects' | 'compliance'
  | 'approvals' | 'risk' | 'zatca' | 'collections' | 'crm';

export interface DrillDownColumnDef {
  key: string;
  label: string;
  format?: 'currency' | 'number' | 'date' | 'text' | 'badge' | 'percent';
  sortable?: boolean;
  sensitive?: boolean; // masked if user lacks permission
}

export interface MetricDefinition {
  metric_key: string;
  dashboard_name: string;
  module: MetricModule;
  display_title: string;
  subtitle?: string;
  source_table: string;
  loader: (companyId?: string | null, filters?: Record<string, unknown>) => Promise<DrilldownResult>;
  default_filters?: Record<string, unknown>;
  allowed_roles: string[];
  export_enabled: boolean;
  sensitive: boolean;
  /** If true, re-auth prompt + audit log entry before showing data */
  require_reauth?: boolean;
  drilldown_columns?: DrillDownColumnDef[];
  route_to_source?: string;
  /** Can drill further into a child metric */
  child_metric_key?: string;
}

// ── Role groups ───────────────────────────────────────────────

const ALL_EXEC = ['super_admin', 'group_ceo', 'cfo', 'coo', 'ceo_commercial', 'ceo_manufacturing'];
const FINANCE = ['super_admin', 'finance_manager', 'finance_ap', 'finance_ar', 'cfo', ...ALL_EXEC];
const HR_ROLES = ['super_admin', 'hr_manager', 'hr_staff', 'chro', ...ALL_EXEC];
const PAYROLL_ROLES = ['super_admin', 'hr_manager', 'chro', 'finance_manager', 'cfo'];
const SALES_ROLES = ['super_admin', 'sales_manager', 'sales_manager_pilot', 'sales_rep', 'sales_rep_pilot', 'ceo_commercial', ...ALL_EXEC];
const PROCUREMENT_ROLES = ['super_admin', 'procurement_manager', 'purchasing_officer', 'coo', ...ALL_EXEC];
const WAREHOUSE_ROLES = ['super_admin', 'warehouse_manager', 'warehouse_staff', 'logistics_manager', ...ALL_EXEC];
const MFG_ROLES = ['super_admin', 'production_manager', 'engineering_manager', 'ceo_manufacturing', ...ALL_EXEC];
const PM_ROLES = ['super_admin', 'project_manager', 'pmo_head', 'coo', ...ALL_EXEC];
const COMPLIANCE_ROLES = ['super_admin', 'compliance_officer', 'chro', 'cfo', ...ALL_EXEC];
const APPROVAL_ROLES = ['super_admin', 'hr_manager', 'finance_manager', 'coo', ...ALL_EXEC];

// ── Registry ──────────────────────────────────────────────────

export const METRIC_REGISTRY: Record<string, MetricDefinition> = {

  // ══════════════════════════════════════════════════════════
  // FINANCE / CFO
  // ══════════════════════════════════════════════════════════

  revenue_mtd: {
    metric_key: 'revenue_mtd',
    dashboard_name: 'Finance Dashboard',
    module: 'finance',
    display_title: 'Revenue (Month to Date)',
    subtitle: 'Paid + issued invoices in current month',
    source_table: 'invoices',
    loader: drillRevenueMtd,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/invoices',
  },

  outstanding_ar: {
    metric_key: 'outstanding_ar',
    dashboard_name: 'Finance Dashboard',
    module: 'finance',
    display_title: 'Outstanding Accounts Receivable',
    subtitle: 'Unpaid customer invoices',
    source_table: 'invoices',
    loader: drillOutstandingAR,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/invoices',
  },

  overdue_receivables: {
    metric_key: 'overdue_receivables',
    dashboard_name: 'Finance Dashboard',
    module: 'collections',
    display_title: 'Overdue Receivables',
    subtitle: 'Customer invoices past due date',
    source_table: 'invoices',
    loader: drillOverdueReceivables,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/invoices',
  },

  outstanding_ap: {
    metric_key: 'outstanding_ap',
    dashboard_name: 'Finance Dashboard',
    module: 'finance',
    display_title: 'Outstanding Accounts Payable',
    subtitle: 'Unpaid supplier invoices',
    source_table: 'supplier_invoices',
    loader: drillOutstandingAP,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/purchasing',
  },

  cash_position: {
    metric_key: 'cash_position',
    dashboard_name: 'Finance Dashboard',
    module: 'finance',
    display_title: 'Cash Position',
    subtitle: 'Bank account balances',
    source_table: 'treasury_accounts',
    loader: drillCashPosition,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: true,
    route_to_source: '/finance/treasury',
  },

  vat_liability: {
    metric_key: 'vat_liability',
    dashboard_name: 'Finance Dashboard',
    module: 'finance',
    display_title: 'VAT Liability',
    subtitle: 'Collected VAT awaiting remittance',
    source_table: 'invoices',
    loader: drillVatLiability,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/finance/vat',
  },

  zatca_rejected: {
    metric_key: 'zatca_rejected',
    dashboard_name: 'ZATCA Dashboard',
    module: 'zatca',
    display_title: 'ZATCA Rejected Invoices',
    subtitle: 'Invoices rejected by ZATCA',
    source_table: 'invoices',
    loader: drillZatcaRejected,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/finance/zatca',
  },

  zatca_gaps: {
    metric_key: 'zatca_gaps',
    dashboard_name: 'ZATCA Dashboard',
    module: 'zatca',
    display_title: 'ZATCA Compliance Gaps',
    subtitle: 'Invoices not submitted or with missing required fields',
    source_table: 'invoices',
    loader: drillZatcaGaps,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/finance/zatca',
  },

  bank_recon_exceptions: {
    metric_key: 'bank_recon_exceptions',
    dashboard_name: 'Finance Dashboard',
    module: 'finance',
    display_title: 'Bank Reconciliation Exceptions',
    subtitle: 'Unmatched transactions in bank statements',
    source_table: 'bank_transactions',
    loader: drillBankReconciliationExceptions,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: true,
    route_to_source: '/finance/bank-recon',
  },

  depreciation_runs: {
    metric_key: 'depreciation_runs',
    dashboard_name: 'Finance Dashboard',
    module: 'finance',
    display_title: 'Depreciation Runs',
    subtitle: 'Fixed asset depreciation posting history',
    source_table: 'depreciation_run_log',
    loader: drillDepreciationRuns,
    allowed_roles: FINANCE,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/finance/fixed-assets',
  },

  ebitda: {
    metric_key: 'ebitda',
    dashboard_name: 'CEO Dashboard',
    module: 'finance',
    display_title: 'EBITDA Components',
    subtitle: 'Earnings breakdown: revenue, COGS, operating costs',
    source_table: 'invoices',
    loader: drillEbitda,
    allowed_roles: [...FINANCE, ...ALL_EXEC],
    export_enabled: true,
    sensitive: false,
  },

  // ══════════════════════════════════════════════════════════
  // CEO DASHBOARD
  // ══════════════════════════════════════════════════════════

  won_deals_mtd: {
    metric_key: 'won_deals_mtd',
    dashboard_name: 'CEO Dashboard',
    module: 'sales',
    display_title: 'Won Deals (Month to Date)',
    subtitle: 'Closed-won quotations this month',
    source_table: 'quotations',
    loader: drillWonDealsMtd,
    allowed_roles: [...SALES_ROLES, ...ALL_EXEC],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/quotations',
  },

  sales_pipeline: {
    metric_key: 'sales_pipeline',
    dashboard_name: 'CEO Dashboard',
    module: 'sales',
    display_title: 'Active Sales Pipeline',
    subtitle: 'Open opportunities by stage',
    source_table: 'opportunities',
    loader: drillSalesPipeline,
    allowed_roles: [...SALES_ROLES, ...ALL_EXEC],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/crm/pipeline',
  },

  active_projects: {
    metric_key: 'active_projects',
    dashboard_name: 'CEO Dashboard',
    module: 'projects',
    display_title: 'Active Projects',
    subtitle: 'Projects currently in execution',
    source_table: 'projects',
    loader: drillActiveProjects,
    allowed_roles: [...PM_ROLES, ...ALL_EXEC],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/projects',
  },

  // ══════════════════════════════════════════════════════════
  // HR / CHRO
  // ══════════════════════════════════════════════════════════

  headcount: {
    metric_key: 'headcount',
    dashboard_name: 'HR Dashboard',
    module: 'hr',
    display_title: 'Active Headcount',
    subtitle: 'All active employees by department',
    source_table: 'employees',
    loader: drillHeadcount,
    allowed_roles: HR_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/employees',
  },

  payroll_cost: {
    metric_key: 'payroll_cost',
    dashboard_name: 'HR Dashboard',
    module: 'payroll',
    display_title: 'Payroll Cost',
    subtitle: 'Approved payroll batches with total net',
    source_table: 'payroll_batches',
    loader: drillPayrollCost,
    allowed_roles: PAYROLL_ROLES,
    export_enabled: true,
    sensitive: true,
    require_reauth: false,
    route_to_source: '/payroll',
  },

  payroll_batches: {
    metric_key: 'payroll_batches',
    dashboard_name: 'Payroll Dashboard',
    module: 'payroll',
    display_title: 'Payroll Batches',
    subtitle: 'All payroll batches with employee-level detail',
    source_table: 'payroll_batches',
    loader: drillPayrollBatches,
    allowed_roles: PAYROLL_ROLES,
    export_enabled: true,
    sensitive: true,
    route_to_source: '/payroll',
  },

  gosi_contributions: {
    metric_key: 'gosi_contributions',
    dashboard_name: 'HR Dashboard',
    module: 'hr',
    display_title: 'GOSI Contributions',
    subtitle: 'Monthly GOSI submissions and contribution totals',
    source_table: 'gosi_submissions',
    loader: drillGosiContributions,
    allowed_roles: [...HR_ROLES, ...FINANCE],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/gosi-report',
  },

  wps_submissions: {
    metric_key: 'wps_submissions',
    dashboard_name: 'HR Dashboard',
    module: 'hr',
    display_title: 'WPS Salary Files',
    subtitle: 'Wage Protection System submission status',
    source_table: 'wps_salary_files',
    loader: drillWpsSubmissions,
    allowed_roles: [...HR_ROLES, ...FINANCE],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/wps-submission',
  },

  leave_balance: {
    metric_key: 'leave_balance',
    dashboard_name: 'HR Dashboard',
    module: 'hr',
    display_title: 'Leave Balance',
    subtitle: 'Pending leave requests by type and employee',
    source_table: 'leave_requests',
    loader: drillLeaveBalance,
    allowed_roles: HR_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/leave',
  },

  document_expiry: {
    metric_key: 'document_expiry',
    dashboard_name: 'HR Dashboard',
    module: 'hr',
    display_title: 'Expiring Documents',
    subtitle: 'Employee documents expiring within 90 days',
    source_table: 'employee_documents',
    loader: drillDocumentExpiry,
    allowed_roles: HR_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/hr/expiry-alerts',
  },

  iqama_expiry: {
    metric_key: 'iqama_expiry',
    dashboard_name: 'HR Dashboard',
    module: 'compliance',
    display_title: 'Iqama Expiry',
    subtitle: 'Non-Saudi employees with expiring residency permits',
    source_table: 'employees',
    loader: drillIqamaExpiry,
    allowed_roles: [...HR_ROLES, ...COMPLIANCE_ROLES],
    export_enabled: true,
    sensitive: true,
    route_to_source: '/employees',
  },

  nitaqat_by_dept: {
    metric_key: 'nitaqat_by_dept',
    dashboard_name: 'Compliance Dashboard',
    module: 'compliance',
    display_title: 'Nitaqat by Department',
    subtitle: 'Saudization percentage per department',
    source_table: 'employees',
    loader: drillNitaqatByDept,
    allowed_roles: [...HR_ROLES, ...COMPLIANCE_ROLES],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/nitaqat',
  },

  training_completion: {
    metric_key: 'training_completion',
    dashboard_name: 'HR Dashboard',
    module: 'hr',
    display_title: 'Training Completion',
    subtitle: 'Training program enrollments and completions',
    source_table: 'training_enrollments',
    loader: drillTrainingCompletion,
    allowed_roles: HR_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/training',
  },

  disciplinary_cases: {
    metric_key: 'disciplinary_cases',
    dashboard_name: 'HR Dashboard',
    module: 'hr',
    display_title: 'Disciplinary Cases',
    subtitle: 'Open disciplinary actions',
    source_table: 'disciplinary_actions',
    loader: drillDisciplinaryCases,
    allowed_roles: HR_ROLES,
    export_enabled: false,
    sensitive: true,
    route_to_source: '/disciplinary',
  },

  // ══════════════════════════════════════════════════════════
  // SALES / CRM
  // ══════════════════════════════════════════════════════════

  leads_by_stage: {
    metric_key: 'leads_by_stage',
    dashboard_name: 'Sales Dashboard',
    module: 'crm',
    display_title: 'Leads by Stage',
    subtitle: 'CRM leads in each pipeline stage',
    source_table: 'crm_leads',
    loader: drillLeadsByStage,
    allowed_roles: SALES_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/crm/leads',
  },

  quotations_by_status: {
    metric_key: 'quotations_by_status',
    dashboard_name: 'Sales Dashboard',
    module: 'sales',
    display_title: 'Quotations',
    subtitle: 'Quotations by status and value',
    source_table: 'quotations',
    loader: drillQuotationsByStatus,
    allowed_roles: SALES_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/quotations',
  },

  sales_rep_performance: {
    metric_key: 'sales_rep_performance',
    dashboard_name: 'Sales Dashboard',
    module: 'sales',
    display_title: 'Sales Rep Performance',
    subtitle: 'Won deals and pipeline value per sales rep',
    source_table: 'quotations',
    loader: drillSalesRepPerformance,
    allowed_roles: [...SALES_ROLES, ...ALL_EXEC],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/quotations',
  },

  commission_payable: {
    metric_key: 'commission_payable',
    dashboard_name: 'Sales Dashboard',
    module: 'sales',
    display_title: 'Commission Payable',
    subtitle: 'Earned commissions pending payment',
    source_table: 'commissions',
    loader: drillCommissionPayable,
    allowed_roles: [...SALES_ROLES, ...PAYROLL_ROLES],
    export_enabled: true,
    sensitive: true,
    route_to_source: '/payroll',
  },

  // ══════════════════════════════════════════════════════════
  // PROCUREMENT
  // ══════════════════════════════════════════════════════════

  open_purchase_orders: {
    metric_key: 'open_purchase_orders',
    dashboard_name: 'Procurement Dashboard',
    module: 'procurement',
    display_title: 'Open Purchase Orders',
    subtitle: 'POs awaiting delivery or invoice',
    source_table: 'purchase_orders',
    loader: drillOpenPurchaseOrders,
    allowed_roles: PROCUREMENT_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/purchasing',
  },

  pending_supplier_invoices: {
    metric_key: 'pending_supplier_invoices',
    dashboard_name: 'Procurement Dashboard',
    module: 'procurement',
    display_title: 'Pending Supplier Invoices',
    subtitle: 'Supplier invoices awaiting approval or payment',
    source_table: 'supplier_invoices',
    loader: drillPendingSupplierInvoices,
    allowed_roles: [...PROCUREMENT_ROLES, ...FINANCE],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/purchasing',
  },

  three_way_match_exceptions: {
    metric_key: 'three_way_match_exceptions',
    dashboard_name: 'Procurement Dashboard',
    module: 'procurement',
    display_title: '3-Way Match Exceptions',
    subtitle: 'Invoices with PO / GRN / Invoice quantity or price discrepancies',
    source_table: 'invoice_match_exceptions',
    loader: drillThreeWayMatchExceptions,
    allowed_roles: [...PROCUREMENT_ROLES, ...FINANCE],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/purchasing',
  },

  // ══════════════════════════════════════════════════════════
  // WAREHOUSE / INVENTORY
  // ══════════════════════════════════════════════════════════

  inventory_value: {
    metric_key: 'inventory_value',
    dashboard_name: 'Warehouse Dashboard',
    module: 'warehouse',
    display_title: 'Inventory Value',
    subtitle: 'All inventory items with on-hand quantity and value',
    source_table: 'inventory_items',
    loader: drillInventoryValue,
    allowed_roles: [...WAREHOUSE_ROLES, ...FINANCE],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/inventory',
  },

  stockouts: {
    metric_key: 'stockouts',
    dashboard_name: 'Warehouse Dashboard',
    module: 'warehouse',
    display_title: 'Stockout Items',
    subtitle: 'Items with zero on-hand quantity',
    source_table: 'inventory_items',
    loader: drillStockouts,
    allowed_roles: WAREHOUSE_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/inventory',
  },

  reorder_alerts: {
    metric_key: 'reorder_alerts',
    dashboard_name: 'Warehouse Dashboard',
    module: 'warehouse',
    display_title: 'Reorder Alerts',
    subtitle: 'Items below reorder point requiring purchase',
    source_table: 'inventory_items',
    loader: drillReorderAlerts,
    allowed_roles: [...WAREHOUSE_ROLES, ...PROCUREMENT_ROLES],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/inventory',
  },

  slow_moving_stock: {
    metric_key: 'slow_moving_stock',
    dashboard_name: 'Warehouse Dashboard',
    module: 'warehouse',
    display_title: 'Slow-Moving Stock',
    subtitle: 'Items with no movement in 90+ days',
    source_table: 'inventory_items',
    loader: drillSlowMovingStock,
    allowed_roles: WAREHOUSE_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/inventory',
  },

  cycle_count_variances: {
    metric_key: 'cycle_count_variances',
    dashboard_name: 'Warehouse Dashboard',
    module: 'warehouse',
    display_title: 'Cycle Count Variances',
    subtitle: 'Items with discrepancy between system and physical count',
    source_table: 'cycle_count_items',
    loader: drillCycleCountVariances,
    allowed_roles: WAREHOUSE_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/inventory',
  },

  // ══════════════════════════════════════════════════════════
  // MANUFACTURING
  // ══════════════════════════════════════════════════════════

  production_orders: {
    metric_key: 'production_orders',
    dashboard_name: 'Manufacturing Dashboard',
    module: 'manufacturing',
    display_title: 'Production Orders',
    subtitle: 'All active and planned production orders',
    source_table: 'production_orders',
    loader: drillProductionOrders,
    allowed_roles: MFG_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/production',
  },

  work_orders: {
    metric_key: 'work_orders',
    dashboard_name: 'Manufacturing Dashboard',
    module: 'manufacturing',
    display_title: 'Work Orders',
    subtitle: 'Open and in-progress work orders',
    source_table: 'work_orders',
    loader: drillWorkOrders,
    allowed_roles: MFG_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/work-orders',
  },

  mrp_exceptions: {
    metric_key: 'mrp_exceptions',
    dashboard_name: 'Manufacturing Dashboard',
    module: 'manufacturing',
    display_title: 'MRP Exceptions',
    subtitle: 'Items with net requirements requiring action',
    source_table: 'mrp_planned_orders',
    loader: drillMrpExceptions,
    allowed_roles: MFG_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/mrp-planning',
  },

  // ══════════════════════════════════════════════════════════
  // PROJECTS / EPM
  // ══════════════════════════════════════════════════════════

  project_budget: {
    metric_key: 'project_budget',
    dashboard_name: 'Project Dashboard',
    module: 'projects',
    display_title: 'Project Budget vs Actual',
    subtitle: 'Budget utilization and variance per project',
    source_table: 'projects',
    loader: drillProjectBudget,
    allowed_roles: [...PM_ROLES, ...FINANCE],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/projects',
  },

  milestone_delays: {
    metric_key: 'milestone_delays',
    dashboard_name: 'Project Dashboard',
    module: 'projects',
    display_title: 'Overdue Milestones',
    subtitle: 'Project milestones past their target date',
    source_table: 'project_milestones',
    loader: drillMilestoneDelays,
    allowed_roles: PM_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/projects',
  },

  project_risks: {
    metric_key: 'project_risks',
    dashboard_name: 'Project Dashboard',
    module: 'risk',
    display_title: 'Project Risks',
    subtitle: 'Open risks by project and severity',
    source_table: 'project_risks',
    loader: drillProjectRisks,
    allowed_roles: [...PM_ROLES, ...COMPLIANCE_ROLES],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/projects',
  },

  // ══════════════════════════════════════════════════════════
  // COMPLIANCE / RISK
  // ══════════════════════════════════════════════════════════

  open_risks: {
    metric_key: 'open_risks',
    dashboard_name: 'Risk Dashboard',
    module: 'risk',
    display_title: 'Open Risks',
    subtitle: 'Active risks in the risk register',
    source_table: 'risk_register',
    loader: drillOpenRisks,
    allowed_roles: [...COMPLIANCE_ROLES, ...ALL_EXEC],
    export_enabled: true,
    sensitive: false,
    route_to_source: '/compliance',
  },

  open_ncrs: {
    metric_key: 'open_ncrs',
    dashboard_name: 'Compliance Dashboard',
    module: 'compliance',
    display_title: 'Open Non-Conformances',
    subtitle: 'Quality NCRs requiring resolution',
    source_table: 'ncr_reports',
    loader: drillOpenNcrs,
    allowed_roles: COMPLIANCE_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/compliance',
  },

  audit_findings: {
    metric_key: 'audit_findings',
    dashboard_name: 'Compliance Dashboard',
    module: 'compliance',
    display_title: 'Audit Findings',
    subtitle: 'Open audit findings awaiting remediation',
    source_table: 'audit_findings',
    loader: drillAuditFindings,
    allowed_roles: COMPLIANCE_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/compliance',
  },

  pdpl_requests: {
    metric_key: 'pdpl_requests',
    dashboard_name: 'Compliance Dashboard',
    module: 'compliance',
    display_title: 'PDPL Data Requests',
    subtitle: 'Personal data requests under Saudi PDPL',
    source_table: 'pdpl_requests',
    loader: drillPdplRequests,
    allowed_roles: COMPLIANCE_ROLES,
    export_enabled: false,
    sensitive: true,
    route_to_source: '/compliance',
  },

  mfa_violations: {
    metric_key: 'mfa_violations',
    dashboard_name: 'Compliance Dashboard',
    module: 'compliance',
    display_title: 'MFA Violations',
    subtitle: 'Users without MFA enabled or with failed MFA events',
    source_table: 'auth_security_events',
    loader: drillMfaViolations,
    allowed_roles: ['super_admin', 'it_admin', 'compliance_officer'],
    export_enabled: false,
    sensitive: true,
    route_to_source: '/admin/security',
  },

  // ══════════════════════════════════════════════════════════
  // APPROVALS
  // ══════════════════════════════════════════════════════════

  pending_approvals: {
    metric_key: 'pending_approvals',
    dashboard_name: 'Approval Dashboard',
    module: 'approvals',
    display_title: 'Pending Approvals',
    subtitle: 'All approvals awaiting action by approver',
    source_table: 'approval_requests',
    loader: drillPendingApprovals,
    allowed_roles: APPROVAL_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/approvals',
  },

  approval_aging: {
    metric_key: 'approval_aging',
    dashboard_name: 'Approval Dashboard',
    module: 'approvals',
    display_title: 'Approval Aging',
    subtitle: 'Approvals overdue by owner and days waiting',
    source_table: 'approval_requests',
    loader: drillApprovalAging,
    allowed_roles: APPROVAL_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/approvals',
  },

  rejected_approvals: {
    metric_key: 'rejected_approvals',
    dashboard_name: 'Approval Dashboard',
    module: 'approvals',
    display_title: 'Rejected Approvals',
    subtitle: 'Approvals rejected in the last 30 days',
    source_table: 'approval_requests',
    loader: drillRejectedApprovals,
    allowed_roles: APPROVAL_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/approvals',
  },

  escalated_approvals: {
    metric_key: 'escalated_approvals',
    dashboard_name: 'Approval Dashboard',
    module: 'approvals',
    display_title: 'Escalated Approvals',
    subtitle: 'Approvals that have been escalated due to SLA breach',
    source_table: 'approval_requests',
    loader: drillEscalatedApprovals,
    allowed_roles: APPROVAL_ROLES,
    export_enabled: true,
    sensitive: false,
    route_to_source: '/approvals',
  },

  // ══════════════════════════════════════════════════════════
  // CRM — Lead & Opportunity Pipeline KPIs
  // ══════════════════════════════════════════════════════════

  crm_new_leads: {
    metric_key: 'crm_new_leads', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'New Leads', subtitle: 'Leads with status = new',
    source_table: 'crm_leads', loader: drillCrmNewLeads,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false,
    route_to_source: '/crm',
    drilldown_columns: [
      { key: 'title', label: 'Lead' }, { key: 'company', label: 'Company' },
      { key: 'source', label: 'Source' }, { key: 'score', label: 'Score', format: 'number' },
      { key: 'assigned_to', label: 'Rep' }, { key: 'created_at', label: 'Created', format: 'date' },
    ],
  },
  crm_qualified_leads: {
    metric_key: 'crm_qualified_leads', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Qualified Leads', subtitle: 'Leads that passed qualification',
    source_table: 'crm_leads', loader: drillCrmQualifiedLeads,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/crm',
  },
  crm_converted_leads: {
    metric_key: 'crm_converted_leads', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Converted Leads', subtitle: 'Leads converted to opportunities/customers',
    source_table: 'crm_leads', loader: drillCrmConvertedLeads,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/crm',
  },
  crm_open_opportunities: {
    metric_key: 'crm_open_opportunities', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Open Opportunities', subtitle: 'Active opportunities not yet won or lost',
    source_table: 'opportunities', loader: drillCrmOpenOpportunities,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/crm',
  },
  crm_pipeline_value: {
    metric_key: 'crm_pipeline_value', dashboard_name: 'Sales Forecast', module: 'crm',
    display_title: 'Total Pipeline Value', subtitle: 'Sum of all open opportunity values',
    source_table: 'opportunities', loader: drillCrmPipelineValue,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/sales-forecast',
  },
  crm_weighted_pipeline: {
    metric_key: 'crm_weighted_pipeline', dashboard_name: 'Sales Forecast', module: 'crm',
    display_title: 'Weighted Pipeline Value', subtitle: 'Pipeline weighted by stage probability',
    source_table: 'sales_pipeline_forecast', loader: drillCrmWeightedPipeline,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/sales-forecast',
  },
  crm_quotation_sent: {
    metric_key: 'crm_quotation_sent', dashboard_name: 'Sales Forecast', module: 'crm',
    display_title: 'Quotations Sent Value', subtitle: 'Value of quotations submitted to customers',
    source_table: 'quotations', loader: drillCrmQuotationSent,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/quotations',
  },
  crm_won_value: {
    metric_key: 'crm_won_value', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Won Value', subtitle: 'Total value of won quotations',
    source_table: 'quotations', loader: drillCrmWonValue,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/quotations',
  },
  crm_lost_value: {
    metric_key: 'crm_lost_value', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Lost Value', subtitle: 'Total value of lost quotations',
    source_table: 'quotations', loader: drillCrmLostValue,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/lost-reason-analysis',
  },
  crm_lost_reasons: {
    metric_key: 'crm_lost_reasons', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Lost Reason Analysis', subtitle: 'Breakdown of lost deals by reason category',
    source_table: 'crm_lost_reason_analysis', loader: drillCrmLostReasons,
    allowed_roles: ['super_admin', 'sales_manager', 'cbo', ...ALL_EXEC],
    export_enabled: true, sensitive: false, route_to_source: '/lost-reason-analysis',
  },
  crm_overdue_activities: {
    metric_key: 'crm_overdue_activities', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Overdue Activities', subtitle: 'Activities past due date not yet completed',
    source_table: 'crm_overdue_activities', loader: drillCrmOverdueActivities,
    allowed_roles: SALES_ROLES, export_enabled: false, sensitive: false, route_to_source: '/crm',
  },
  crm_stale_opportunities: {
    metric_key: 'crm_stale_opportunities', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Stale Opportunities', subtitle: 'Opportunities with no stage change in 14+ days',
    source_table: 'opportunities', loader: drillCrmStaleOpportunities,
    allowed_roles: SALES_ROLES, export_enabled: false, sensitive: false, route_to_source: '/crm',
  },
  crm_expiring_quotations: {
    metric_key: 'crm_expiring_quotations', dashboard_name: 'Sales Forecast', module: 'crm',
    display_title: 'Expiring Quotations', subtitle: 'Quotations expiring within 14 days',
    source_table: 'quotation_expiry_alerts', loader: drillCrmExpiringQuotations,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/sales-forecast',
  },
  crm_target_achievement: {
    metric_key: 'crm_target_achievement', dashboard_name: 'Sales Targets', module: 'crm',
    display_title: 'Sales Target Achievement', subtitle: 'Actual vs target for current month per rep',
    source_table: 'sales_targets', loader: drillCrmTargetAchievement,
    allowed_roles: SALES_ROLES, export_enabled: true, sensitive: false, route_to_source: '/sales-targets',
  },
  crm_revenue_by_channel: {
    metric_key: 'crm_revenue_by_channel', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Revenue by Channel', subtitle: 'Won deal value broken down by sales channel',
    source_table: 'quotations', loader: drillCrmRevenueByChannel,
    allowed_roles: ['super_admin', 'sales_manager', 'cbo', ...ALL_EXEC],
    export_enabled: true, sensitive: false, route_to_source: '/quotations',
  },
  crm_revenue_by_sector: {
    metric_key: 'crm_revenue_by_sector', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Revenue by Sector', subtitle: 'Won deal value broken down by customer sector',
    source_table: 'quotations', loader: drillCrmRevenueBySector,
    allowed_roles: ['super_admin', 'sales_manager', 'cbo', ...ALL_EXEC],
    export_enabled: true, sensitive: false, route_to_source: '/customers',
  },
  crm_margin_exceptions: {
    metric_key: 'crm_margin_exceptions', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Margin Exception Count', subtitle: 'Active quotations with margin below 35% floor',
    source_table: 'quotations', loader: drillCrmMarginExceptions,
    allowed_roles: ['super_admin', 'sales_manager', 'cbo', 'cfo', 'finance_manager', ...ALL_EXEC],
    export_enabled: true, sensitive: true, route_to_source: '/quotations',
  },
  crm_pending_approvals: {
    metric_key: 'crm_pending_approvals', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Pending Approvals', subtitle: 'Quotations awaiting approval at any stage',
    source_table: 'quotations', loader: drillCrmPendingApprovals,
    allowed_roles: ['super_admin', 'sales_manager', 'cbo', 'cfo', 'finance_manager', ...ALL_EXEC],
    export_enabled: false, sensitive: false, route_to_source: '/approvals',
  },
  crm_credit_hold: {
    metric_key: 'crm_credit_hold', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Credit Hold Customers', subtitle: 'Customers with active credit holds',
    source_table: 'customers', loader: drillCrmCreditHoldCustomers,
    allowed_roles: ['super_admin', 'sales_manager', 'cbo', 'cfo', 'finance_manager', 'finance_ar', ...ALL_EXEC],
    export_enabled: false, sensitive: true, route_to_source: '/customers',
  },
  crm_collection_risk: {
    metric_key: 'crm_collection_risk', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Collection Risk — Won Deals', subtitle: 'Finance-approved won deals awaiting collection',
    source_table: 'won_deal_approvals', loader: drillCrmCollectionRisk,
    allowed_roles: ['super_admin', 'cfo', 'finance_manager', 'finance_ar', ...ALL_EXEC],
    export_enabled: true, sensitive: true, route_to_source: '/won-deals',
  },

  // ── Sprint 1: Revenue by product category ──────────────────────
  crm_revenue_by_product_category: {
    metric_key: 'crm_revenue_by_product_category', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Revenue by Product Category', subtitle: 'Won deal revenue broken down by product category',
    source_table: 'v_revenue_by_product_category', loader: drillCrmRevenueByProductCategory,
    allowed_roles: ['super_admin', 'sales_manager', 'cbo', ...ALL_EXEC],
    export_enabled: true, sensitive: false, route_to_source: '/quotations',
  },

  // ── Sprint 2: Dormant customers ─────────────────────────────────
  crm_dormant_customers: {
    metric_key: 'crm_dormant_customers', dashboard_name: 'CRM Analytics', module: 'crm',
    display_title: 'Dormant Customers', subtitle: 'Customers with no activity in 90+ days and no open opportunities',
    source_table: 'v_dormant_customers', loader: drillCrmDormantCustomers,
    allowed_roles: SALES_ROLES, export_enabled: false, sensitive: false, route_to_source: '/customers',
  },
};

// ── Helpers ───────────────────────────────────────────────────

/** Resolve display title from registry (falls back to metric_key) */
export function getMetricTitle(metricKey: string): string {
  return METRIC_REGISTRY[metricKey]?.display_title ?? metricKey.replace(/_/g, ' ');
}

/** Check if a role is allowed for a metric */
export function isMetricAllowed(metricKey: string, role: string): boolean {
  // hasOwnProperty guard: keys like '__proto__' resolve to Object.prototype on
  // a plain-object registry (truthy but without allowed_roles) and must be
  // rejected, not crash.
  if (!Object.prototype.hasOwnProperty.call(METRIC_REGISTRY, metricKey)) return false;
  const def = METRIC_REGISTRY[metricKey];
  if (!def) return false;
  return def.allowed_roles.includes(role);
}

/** Get all metrics for a specific dashboard */
export function getMetricsForDashboard(dashboardName: string): MetricDefinition[] {
  return Object.values(METRIC_REGISTRY).filter(m => m.dashboard_name === dashboardName);
}

/** Get all sensitive metrics (require special handling) */
export function getSensitiveMetrics(): MetricDefinition[] {
  return Object.values(METRIC_REGISTRY).filter(m => m.sensitive);
}
